import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Feather, Clock, Users, ArrowLeft, MailCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail, forgotPassword, resetPassword } from "../lib/api";
import PasswordField from "../components/PasswordField";
import Logo from "../components/Logo";
import TrustPrivacySheet from "../components/TrustPrivacySheet";
import { InstallAffordance } from "../components/InstallAffordance";

const MODES = [
  { key: "login", label: "Sign in" },
  { key: "register", label: "Create account" },
  { key: "join", label: "Join with code" },
];

const inputCls =
  "w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-[var(--ink)] text-base outline-none focus:border-[var(--pine)] focus:ring-2 focus:ring-[var(--pine-soft)] transition-all duration-200 placeholder:text-[var(--ink5)]";

const PILLARS = [
  { icon: Feather, title: "Out of your head", body: "Say it, type it, or paste it. Heldall holds it until it needs to come back." },
  { icon: Clock, title: "Back when it matters", body: "Whether it has a time, a window, or just needs to stay close." },
  { icon: Users, title: "Pass it on when you're ready", body: "Hand it to someone and know when they've marked it done or handed it back." },
];

export default function Auth() {
  const { login, register, adoptSession } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "", invite_code: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [linkBroken, setLinkBroken] = useState(false); // reset link expired/invalid/used
  const [sent, setSent] = useState(false); // forgot-password confirmation
  const [resetToken, setResetToken] = useState("");
  const [trustOpen, setTrustOpen] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Deep links: /reset-password?token=… opens the reset view; /join?code=… opens Join.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (window.location.pathname.includes("reset-password") || token) {
      if (token) {
        setResetToken(token);
        setMode("reset");
        return;
      }
    }
    const code = (params.get("code") || "").trim().toUpperCase();
    if (code) {
      // Preserve the invite intent across sign-in / account creation / refresh: the join is
      // completed by JoinInviteSheet once the user is authenticated (handles existing users too).
      try { localStorage.setItem("heldall_pending_invite", code); } catch (_) {}
      setMode("join");
      setForm((f) => ({ ...f, invite_code: code }));
    }
  }, []);

  const goMode = (m) => { setMode(m); setError(""); setEmailExists(false); setLinkBroken(false); setSent(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setEmailExists(false);
    setLinkBroken(false);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else if (mode === "register") {
        await register({ name: form.name, email: form.email, password: form.password });
        toast.success("Your household is ready.");
      } else if (mode === "join") {
        // Keep the code so the join still completes if this email already has an account
        // (existing user → "Sign in instead" → JoinInviteSheet picks it up after login).
        try { localStorage.setItem("heldall_pending_invite", (form.invite_code || "").trim().toUpperCase()); } catch (_) {}
        await register({
          name: form.name, email: form.email, password: form.password,
          invite_code: form.invite_code,
        });
        toast.success("You're in. Your household is ready.");
      } else if (mode === "forgot") {
        await forgotPassword(form.email);
        setSent(true);
      } else if (mode === "reset") {
        const data = await resetPassword(resetToken, form.password);
        adoptSession(data);
        try { window.history.replaceState({}, "", "/"); } catch (_) {}
        toast.success("Password updated. You're signed in.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = formatApiErrorDetail(detail) || err.message;
      // Friendlier password-length copy (pydantic min_length message is clunky).
      if (/at least 6 characters/i.test(msg)) msg = "Password must be at least 6 characters.";
      setError(msg);
      // Existing-email on register/join → offer a direct path to Sign in.
      if ((mode === "register" || mode === "join") &&
          typeof detail === "string" && detail.toLowerCase().includes("already exists")) {
        setEmailExists(true);
      }
      // A dead reset link (expired / invalid / already used) → offer a fresh link.
      if (mode === "reset" && typeof detail === "string" &&
          /reset link|no longer valid|already been used/i.test(detail)) {
        setLinkBroken(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const heading = {
    login: "Welcome back",
    register: "Start with one thing",
    join: "Join a household",
    forgot: "Reset your password",
    reset: "Choose a new password",
  }[mode];
  const sub = {
    login: "Let's pick up where you left off.",
    register: "Just say what's on your mind. The rest is held.",
    join: "Enter the code someone shared with you.",
    forgot: "Enter your email and we'll send you a link to set a new one.",
    reset: "Pick something you'll remember. You'll be signed in straight away.",
  }[mode];

  const submitLabel = {
    login: "Sign in", register: "Create account", join: "Join household",
    forgot: "Send reset link", reset: "Set new password",
  }[mode];

  const showTabs = mode === "login" || mode === "register" || mode === "join";

  return (
    <div className="min-h-screen paper-grad grid lg:grid-cols-[1.05fr_1fr]">
      {/* Left — invitation / brand story (desktop) */}
      <div className="hidden lg:flex flex-col justify-between px-14 xl:px-20 py-14">
        <div className="flex items-center gap-3">
          <Logo size={32} />
        </div>

        <div className="max-w-lg">
          <h1 className="font-heading text-4xl xl:text-[3.25rem] leading-[1.05] tracking-tight text-[var(--ink)] font-medium">
            You don't have to hold all of it.
          </h1>
          <p className="text-[var(--ink2)] text-lg mt-5 leading-relaxed">
            Say it once. It won't get lost.
          </p>

          <div className="mt-12 space-y-6">
            {PILLARS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex items-start gap-4">
                <span className="mt-0.5 h-10 w-10 shrink-0 rounded-xl bg-[var(--surface)] border border-[var(--line)] flex items-center justify-center text-[var(--pine)] shadow-[0_2px_10px_rgba(26,28,26,0.04)]">
                  <Icon size={18} strokeWidth={1.6} />
                </span>
                <div>
                  <p className="font-heading font-medium text-[var(--ink)]">{title}</p>
                  <p className="text-sm text-[var(--ink3)] mt-0.5 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs font-medium text-[var(--ink3)]">Less to carry. Nothing lost.</p>
      </div>

      {/* Right — the form */}
      <div className="flex items-center justify-center px-6 py-12 lg:bg-[var(--surface)] lg:border-l lg:border-[var(--line)]">
        <div className="w-full max-w-[400px]">
          <div className="mb-7 lg:hidden">
            <Logo size={28} />
            <h1 className="font-heading text-xl font-medium tracking-tight text-[var(--ink)] mt-3 leading-snug">
              You don't have to hold all of it.
            </h1>
            <p className="text-sm text-[var(--ink3)] mt-1.5 leading-relaxed">
              Say it once. It won't get lost.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3.5">
              {PILLARS.map(({ icon: Icon, title }) => (
                <span key={title} className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--ink4)]">
                  <Icon size={13} strokeWidth={1.8} className="text-[var(--pine)]" />
                  {title}
                </span>
              ))}
            </div>
          </div>

          <div className="rise-in rounded-3xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_8px_40px_rgba(26,28,26,0.06)] p-7 sm:p-8">
            {showTabs && (
              <div className="inline-flex items-center gap-0.5 rounded-full bg-[var(--surface2)] p-1 mb-7 w-full" data-testid="auth-tabs">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    data-testid={`auth-tab-${m.key}`}
                    onClick={() => goMode(m.key)}
                    className={`flex-1 rounded-full px-2 py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap leading-none transition-all duration-200 ${
                      mode === m.key ? "bg-[var(--surface)] text-[var(--ink)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]" : "text-[var(--ink3)] hover:text-[var(--ink2)]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {!showTabs && (
              <button
                type="button"
                data-testid="auth-back-to-login"
                onClick={() => goMode("login")}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors duration-200 mb-5"
              >
                <ArrowLeft size={14} strokeWidth={2} /> Back to sign in
              </button>
            )}

            <h2 className="font-heading text-2xl font-medium tracking-tight text-[var(--ink)]">{heading}</h2>
            <p className="text-sm text-[var(--ink3)] mt-1.5 mb-7 leading-relaxed">{sub}</p>

            {mode === "forgot" && sent ? (
              <div data-testid="forgot-sent" className="text-sm text-[var(--pine-dark)] bg-[var(--slate-soft)] rounded-xl px-4 py-4 leading-relaxed">
                <MailCheck size={18} className="mb-2 text-[var(--pine)]" />
                If an account exists for <span className="font-semibold">{form.email}</span>, a reset link is
                on its way. It expires in an hour. If it doesn't arrive, check your junk folder.
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="auth-form" autoComplete="on">
                {(mode === "register" || mode === "join") && (
                  <input data-testid="auth-name" className={inputCls} placeholder="First name"
                    value={form.name} onChange={set("name")} required autoComplete="given-name" />
                )}

                {mode !== "reset" && (
                  <input data-testid="auth-email" type="email" className={inputCls} placeholder="Email"
                    name="email" value={form.email} onChange={set("email")} required
                    autoComplete={mode === "login" ? "username" : "email"} />
                )}

                {mode !== "forgot" && (
                  <PasswordField
                    value={form.password}
                    onChange={set("password")}
                    placeholder={mode === "reset" ? "New password" : "Password"}
                    minLength={mode === "login" ? undefined : 6}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                  />
                )}

                {(mode === "register" || mode === "join" || mode === "reset") && (
                  <p className="text-xs text-[var(--ink4)] -mt-1.5 px-1" data-testid="auth-password-hint">
                    At least 6 characters.
                  </p>
                )}

                {mode === "join" && (
                  <input data-testid="auth-invite-code" className={`${inputCls} tracking-[0.3em] uppercase font-semibold`}
                    placeholder="INVITE CODE" value={form.invite_code}
                    onChange={(e) => setForm((f) => ({ ...f, invite_code: e.target.value.toUpperCase() }))}
                    required />
                )}

                {error && (
                  <div className="text-sm text-[var(--pine-dark)] bg-[var(--slate-soft)] border border-[var(--slate-soft)] rounded-xl px-3.5 py-2.5" data-testid="auth-error">
                    <p>{error}</p>
                    {emailExists && (
                      <button
                        type="button"
                        data-testid="auth-goto-signin"
                        onClick={() => { const em = form.email; goMode("login"); setForm((f) => ({ ...f, email: em, password: "" })); }}
                        className="mt-2 inline-flex items-center gap-1.5 font-semibold text-[var(--pine)] hover:text-[var(--pine-dark)] transition-colors duration-200"
                      >
                        Sign in instead <ArrowRight size={14} strokeWidth={2} />
                      </button>
                    )}
                    {linkBroken && (
                      <button
                        type="button"
                        data-testid="auth-request-new-link"
                        onClick={() => { const em = form.email; goMode("forgot"); setForm((f) => ({ ...f, email: em })); }}
                        className="mt-2 inline-flex items-center gap-1.5 font-semibold text-[var(--pine)] hover:text-[var(--pine-dark)] transition-colors duration-200"
                      >
                        Send a new link <ArrowRight size={14} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                )}

                <button data-testid="auth-submit" type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-50 text-[var(--on-pine)] font-semibold px-6 py-3.5 text-sm shadow-[0_10px_28px_rgba(44,90,77,0.28)] transition-all duration-200 mt-1">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} strokeWidth={2} />}
                  {submitLabel}
                </button>

                {mode === "login" && (
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => goMode("forgot")}
                    className="w-full text-center text-xs font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors duration-200 pt-1"
                  >
                    Forgot password?
                  </button>
                )}
              </form>
            )}

            {showTabs && (
              <p className="text-center text-xs text-[var(--ink4)] mt-6 leading-relaxed">
                {mode === "login"
                  ? "New here? Create an account, or join someone using their invite code."
                  : "Your thoughts stay private unless you choose to share them."}
              </p>
            )}

            <div className="text-center mt-5">
              <button
                type="button"
                data-testid="auth-trust-privacy-link"
                onClick={() => setTrustOpen(true)}
                className="text-xs font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors duration-200"
              >
                Your data &amp; privacy
              </button>
            </div>
          </div>

          <InstallAffordance variant="auth" className="mt-4" />
        </div>
      </div>
      <TrustPrivacySheet open={trustOpen} onClose={() => setTrustOpen(false)} />
    </div>
  );
}
